import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Trash2, Send, Plus, LayoutGrid, Maximize2, Quote } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { commentsService, Comment } from '../../lib/comments'
import { discussionsService, AttachmentData } from '../../lib/discussions'
import { AttachmentSelector } from '../discussions/AttachmentSelector'
import { ItemCard } from '../contributions/ItemCard'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel
} from '../ui/alert-dialog'
import { formatDistanceToNow } from 'date-fns'

interface CommentsPanelProps {
  contributionId: string
  currentUserId: string
  currentUser?: {
    first_name: string
    last_name: string
    username: string
    avatar_url: string
  }
  ownerComment?: string // "Why they shared it" comment
  ownerUser?: {
    first_name: string
    last_name: string
    username: string
    avatar_url: string
  }
  onClose?: () => void
  isMobile?: boolean
  onCommentAdded?: () => void
  onCommentDeleted?: () => void
  onExpand?: () => void
  itemTitle?: string
  itemImageUrl?: string
}

export function CommentsPanel({
  contributionId,
  currentUserId,
  currentUser,
  ownerComment,
  ownerUser,
  onClose,
  isMobile = false,
  onCommentAdded,
  onCommentDeleted,
  onExpand,
  itemTitle,
  itemImageUrl
}: CommentsPanelProps) {
  const navigate = useNavigate()
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showSelector, setShowSelector] = useState(false)
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null)
  const [stagedAttachment, setStagedAttachment] = useState<{
    type: 'contribution' | 'curation'
    id: string
    title: string
    brand?: string
    imageUrl: string | null
  } | null>(null)
  const [attachmentMap, setAttachmentMap] = useState<Map<string, AttachmentData>>(new Map())
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await commentsService.fetchComments(contributionId)
      // Reverse to oldest-first for chat-style display (fetchComments returns newest-first)
      setComments([...data].reverse())
      // Resolve attachment data for any comments that have attachments
      const withAttachments = data.filter(c => c.attachment_id && c.attachment_type)
      if (withAttachments.length > 0) {
        const resolved = await discussionsService.resolveAttachments(
          withAttachments.map(c => ({
            id: c.id,
            content: c.content,
            created_at: c.created_at,
            user_id: c.user_id,
            attachment_type: c.attachment_type,
            attachment_id: c.attachment_id,
          }))
        )
        setAttachmentMap(resolved)
      }
      // Scroll to bottom after messages load (chat UX)
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight })
        }
      }, 100)
    } catch (error) {
      console.error('Failed to fetch comments:', error)
    } finally {
      setIsLoading(false)
    }
  }, [contributionId])

  useEffect(() => {
    fetchComments()

    // Subscribe to new comments from other users
    const subscription = commentsService.subscribeToComments(contributionId, (incomingComment) => {
      setComments((prev) => {
        const exists = prev.some(c => c.id === incomingComment.id)
        if (exists) return prev
        return [...prev, incomingComment]
      })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [contributionId, fetchComments])

  // Auto-focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 400)
    return () => clearTimeout(timer)
  }, [])

  // Auto-resize textarea
  const handleTextareaInput = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if ((!newComment.trim() && !stagedAttachment) || isSubmitting) return

    const commentContent = newComment.trim()
    const attachment = stagedAttachment

    setNewComment('')
    setStagedAttachment(null)
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }

    try {
      setIsSubmitting(true)

      const createdComment = await commentsService.createComment(
        contributionId,
        currentUserId,
        commentContent || (attachment ? `Shared: ${attachment.title}` : ''),
        attachment ? { type: attachment.type, id: attachment.id } : null
      )

      setComments((prev) => [...prev, createdComment])

      // Add attachment to local map for immediate rendering
      if (attachment) {
        setAttachmentMap(prev => {
          const next = new Map(prev)
          next.set(attachment.id, {
            type: attachment.type,
            id: attachment.id,
            title: attachment.title,
            brand: attachment.brand,
            imageUrl: attachment.imageUrl,
          })
          return next
        })
      }

      // Scroll to bottom to show the new message
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
      }

      onCommentAdded?.()
    } catch (error) {
      console.error('Failed to post comment:', error)
      setNewComment(commentContent)
      setStagedAttachment(attachment)
    } finally {
      setIsSubmitting(false)
      inputRef.current?.focus()
    }
  }

  const confirmDelete = async () => {
    if (!deleteCommentId) return
    const commentId = deleteCommentId
    setDeleteCommentId(null)
    try {
      setComments((prev) => prev.filter(c => c.id !== commentId))
      await commentsService.deleteComment(commentId)
      onCommentDeleted?.()
    } catch (error) {
      console.error('Failed to delete comment:', error)
      fetchComments()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const renderAttachment = (comment: Comment) => {
    if (!comment.attachment_id || !comment.attachment_type) return null
    const att = attachmentMap.get(comment.attachment_id)
    if (!att) return null

    if (att.type === 'contribution') {
      const ci = att.contributionItem || { id: att.id, title: att.title, brand: att.brand, cached_image_url: att.imageUrl }
      return (
        <div className="mt-1.5 max-w-[75%] ml-auto">
          <ItemCard
            item={{
              id: ci.id,
              title: ci.title,
              brand: ci.brand,
              cached_image_url: ci.cached_image_url,
              source_url: ci.source_url,
              comment: ci.comment,
              created_at: ci.created_at,
              isSaved: ci.is_saved,
              saves_count: ci.saves_count || 0,
              comments_count: ci.comments_count || 0,
              user: ci.user,
            }}
            currentUserId={currentUserId}
            currentUser={currentUser ? { avatar_url: currentUser.avatar_url, first_name: currentUser.first_name } : undefined}
          />
        </div>
      )
    }

    // Curation card
    return (
      <div
        onClick={() => {
          if (att.ownerUsername && att.slug) navigate(`/profile/${att.ownerUsername}/issues/${att.slug}`)
        }}
        className="mt-1.5 max-w-[75%] ml-auto bg-white overflow-hidden cursor-pointer hover:opacity-95 transition-opacity duration-200"
        style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)' }}
      >
        {att.imageUrl ? (
          <img src={att.imageUrl} alt={att.title} className="w-full h-auto object-cover block" />
        ) : (
          <div className="w-full bg-gray-100 flex items-center justify-center" style={{ aspectRatio: '1' }}>
            <LayoutGrid className="h-8 w-8 text-gray-300" />
          </div>
        )}
        <div className="p-3">
          <h3 className="text-sm text-gray-900 line-clamp-2" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 400 }}>
            {att.title}
          </h3>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-1">Curation</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col h-full min-h-0 bg-white ${isMobile ? '' : 'border-l'}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        {itemTitle ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {itemImageUrl && (
              <img
                src={itemImageUrl}
                alt={itemTitle}
                className="w-10 h-10 rounded object-cover flex-shrink-0"
              />
            )}
            <p className="text-sm font-medium text-gray-900 truncate" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              {itemTitle}
            </p>
          </div>
        ) : (
          <h3 className="text-lg font-semibold flex-1" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
            Comments
          </h3>
        )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {onExpand && (() => {
            const hasContent = comments.length > 0 || !!ownerComment
            return (
              <button
                onClick={hasContent ? onExpand : undefined}
                disabled={!hasContent}
                className={`p-2 rounded-full transition-colors ${
                  !hasContent
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'hover:bg-gray-100'
                }`}
                title={!hasContent ? 'Comment first!' : 'Open in discussions'}
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            )
          })()}
          {!isMobile && onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Comments List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="text-sm text-gray-500">Loading comments...</div>
          </div>
        )}

        {!isLoading && (
          <>
            {/* Start of conversation marker */}
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Start of Conversation
                </span>
              </div>
            </div>

            {/* Sharing context — "Why they shared" pinned first message */}
            {ownerComment && ownerUser && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mx-auto max-w-[85%] mb-2"
              >
                <div className="rounded-2xl bg-[#F8F6F0] border border-[#E8E4DA] px-5 py-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Quote className="h-3.5 w-3.5 text-[#A0937D]" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#A0937D]">
                      Why they shared
                    </span>
                  </div>
                  <p className="text-sm text-[#5A5347] italic leading-relaxed">
                    &ldquo;{ownerComment}&rdquo;
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    {ownerUser.avatar_url ? (
                      <img
                        src={ownerUser.avatar_url}
                        alt={ownerUser.first_name}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-[#E8E4DA] flex items-center justify-center">
                        <User className="h-3 w-3 text-[#A0937D]" />
                      </div>
                    )}
                    <span className="text-[12px] font-medium text-[#8B7E6A]">
                      {ownerUser.first_name} {ownerUser.last_name}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Empty state */}
            {comments.length === 0 && (
              <div className="flex justify-center py-8">
                <div className="text-sm text-gray-500">
                  {ownerComment ? 'Be the first to respond!' : 'No comments yet. Be the first to comment!'}
                </div>
              </div>
            )}

            {/* Comments as chat bubbles */}
            <AnimatePresence initial={false}>
              {comments.map((comment) => {
                const isOwn = comment.user_id === currentUserId
                return (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`flex gap-2.5 group ${isOwn ? 'flex-row-reverse' : ''}`}
                  >
                    {/* Avatar — only for other users */}
                    {!isOwn && (
                      comment.user?.avatar_url ? (
                        <img
                          src={comment.user.avatar_url}
                          alt={comment.user.first_name}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-gray-500" />
                        </div>
                      )
                    )}

                    {/* Bubble */}
                    <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
                      {!isOwn && (
                        <p className="text-[11px] font-semibold text-gray-500 mb-1 px-1">
                          {comment.user?.first_name || 'Unknown'}
                        </p>
                      )}
                      <div
                        className={`relative rounded-2xl px-4 py-2.5 ${
                          isOwn
                            ? 'bg-[#57241F] text-white rounded-br-md'
                            : 'bg-white border border-gray-100 text-gray-900 rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{comment.content}</p>
                        {/* Delete button */}
                        {isOwn && (
                          <button
                            onClick={() => setDeleteCommentId(comment.id)}
                            className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-30 hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-500"
                            title="Delete comment"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      {/* Attachment — rendered outside the bubble to match discussions page */}
                      {renderAttachment(comment)}
                      <p className={`text-[10px] text-gray-400 mt-1 ${isOwn ? 'text-right' : ''} px-1`}>
                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Staged Attachment Preview */}
      {stagedAttachment && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
          <span className="text-xs text-gray-500">Sharing:</span>
          <span className="text-xs font-medium text-gray-700 truncate flex-1">{stagedAttachment.title}</span>
          <button
            onClick={() => setStagedAttachment(null)}
            className="p-0.5 hover:bg-gray-200 rounded transition-colors"
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>
      )}

      {/* Comment Input — matching discussion conversation layout */}
      <form onSubmit={handleSubmit} className="border-t p-3">
        <div className="flex items-end gap-2">
          {/* Current User Avatar */}
          {currentUser?.avatar_url ? (
            <img
              src={currentUser.avatar_url}
              alt={currentUser.first_name}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0 mb-0.5"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mb-0.5">
              <User className="h-4 w-4 text-gray-500" />
            </div>
          )}

          {/* Textarea */}
          <textarea
            ref={inputRef}
            value={newComment}
            onChange={(e) => { setNewComment(e.target.value); handleTextareaInput() }}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment..."
            rows={1}
            className="flex-1 resize-none px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-sm leading-relaxed"
            disabled={isSubmitting}
            maxLength={2000}
          />

          {/* Attach button */}
          <div className="relative group/attach flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowSelector(true)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
            </button>
            <div className="absolute bottom-full right-0 mb-1.5 px-2.5 py-1 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/attach:opacity-100 pointer-events-none transition-opacity">
              Share an Item/Curation
            </div>

            <AnimatePresence>
              {showSelector && (
                <AttachmentSelector
                  userId={currentUserId}
                  onSelect={(item) => {
                    setStagedAttachment(item)
                    setShowSelector(false)
                    inputRef.current?.focus()
                  }}
                  onClose={() => setShowSelector(false)}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Send button */}
          <button
            type="submit"
            disabled={(!newComment.trim() && !stagedAttachment) || isSubmitting}
            className="p-2 text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>

      {/* Delete confirmation modal */}
      <AlertDialog open={!!deleteCommentId} onOpenChange={(open) => { if (!open) setDeleteCommentId(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
